import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CommonTableList from '../common/common-table-list';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, PageSizeSelect } from '../common/table-pagination';
import CommonDetail from '../common/common-detail';
import ProductDetail from './product-detail';
import { initProduct, product } from '../../model/master';
import { api_product } from '../../api/product';
import { isEmpty } from '../../utils/util';
import { Button, Modal } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import '../../assets/css/stock-alert.css';
import { deleteFailureMessage } from '../../utils/write-result';

const Product: React.FC = (prop) => {

    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [mode, setMode] = useState('');
    const [title, setTitle] = useState('');
    const [data, setData] = useState<product[]>([]);
    const [product, setProduct] = useState<product>(initProduct);
    const [stockFilter, setStockFilter] = useState<'ALL' | 'OUT' | 'LOW' | 'NORMAL' | 'NEVER_STOCKED'>('ALL');

    /**
     * Counts for the cards, from the server, over the whole catalogue.
     *
     * These used to be counted from `data` -- the rows of the current page
     * -- while the "total" card read `pagination.total` from the server, so
     * the cards contradicted each other. At ten rows a page a catalogue of
     * 2,610 products reported "2610 total, 9 low, 1 normal". The server now
     * counts all four numbers over the same WHERE the list uses.
     */
    const [counts, setCounts] = useState<{ total: number; out: number; low: number; normal: number; never_stocked: number }>({
        total: 0, out: 0, low: 0, normal: 0, never_stocked: 0,
    });

    const filterField: any = ['product_code', 'product_name', 'product_name_other', 'vendor_name'];

    const placeholder_txt: string = 'รหัสสินค้า / ชื่อสินค้า / ชื่อสินค้าอื่นๆ / ผู้ขายส่งสินค้า';

    /**
     * The low-stock rule is no longer re-derived here.
     *
     * This screen used to hold its own copy (`quantity <= limit_quantity`,
     * defaulting to 10) while Backend held another, and the inventory
     * report held a third that used a different column entirely. Backend
     * now returns `stock_state` per row and the counts for the cards, so
     * there is one rule and the screens cannot drift apart again.
     *
     * Filtering is server-side too: clicking a card used to filter only the
     * rows already on screen, so "สินค้าใกล้หมด" showed whichever of the
     * current ten rows happened to qualify.
     */
    const filteredData = data;

    const columns: ColumnsType<product> = [
        { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', fixed: 'left' },
        { title: 'ชื่อสินค้า', dataIndex: 'product_name', key: 'product_name', fixed: 'left' },
        { title: 'ชื่อสินค้าอื่นๆ', dataIndex: 'product_name_other', key: 'product_name_other' },
        { title: 'ประเภทสินค้า', dataIndex: 'product_type_name', key: 'product_type_name' },
        { title: 'หน่วยสินค้า', dataIndex: 'product_unit_name', key: 'product_unit_name' },
        { 
            title: 'จำนวนคงเหลือ', 
            dataIndex: 'quantity', 
            key: 'quantity',
            width: '170px',
            render: (qty: number, record: product) => {
                // The badge follows `stock_state` from Backend rather than
                // re-deriving the threshold here, which is how this screen
                // and the inventory report came to disagree.
                //
                // "ยังไม่ได้ลงสต๊อก" is the state that used to be hidden
                // inside "ใกล้หมด": no row in `stock` at all, i.e. never
                // received, which `COALESCE(quantity, 0)` rendered as 0.
                const state = (record as any).stock_state as string | undefined;
                const currentQty = Number(qty || 0);
                // "จุดสั่งซื้อ" on the product form -- the field whose label
                // already said what it was for, and which nothing read.
                // Shown only in the tooltip, and only when the shop set one.
                const limit = record.point_purchase;

                const chip = (bg: string, color: string, border: string, text: string, title?: string) => (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '14px' }}>
                            {state === 'NEVER_STOCKED' ? '-' : currentQty}
                        </span>
                        <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            background: bg,
                            color: color,
                            border: `1px solid ${border}`,
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '11.5px',
                            fontWeight: 700
                        }} title={title}>
                            {text}
                        </span>
                    </div>
                );

                if (state === 'NEVER_STOCKED') {
                    return chip('#F1F5F9', '#475569', '#CBD5E1', '📥 ยังไม่ได้ลงสต๊อก',
                        'ยังไม่เคยรับเข้าสินค้านี้ จึงยังไม่มีข้อมูลสต๊อก');
                }
                // Out of stock is a fact and warns on its own -- checked
                // before LOW, or a product at zero with no threshold set
                // would fall through and read "ปกติ" while holding nothing.
                if (state === 'OUT' || (state === undefined && currentQty <= 0)) {
                    return chip('#FEF2F2', '#DC2626', '#FECACA', '🔴 สินค้าหมด');
                }
                if (state === 'LOW') {
                    return chip('#FFF7ED', '#C2410C', '#FFEDD5', '⚠️ ใกล้หมด', `จุดเตือนสต๊อก: ${limit}`);
                }
                return chip('#ECFDF5', '#059669', '#A7F3D0', '🟢 ปกติ',
                    limit === null || limit === undefined || limit === ''
                        ? 'ยังไม่ได้ตั้งจุดเตือนสต๊อก จึงไม่มีการเตือนล่วงหน้า'
                        : `จุดเตือนสต๊อก: ${limit}`);
            }
        },
        { title: 'ราคาต้นทุน', dataIndex: 'product_cost', key: 'product_cost', width: '130px' },
        { title: 'ราคาขาย (ปลีก)', dataIndex: 'price_retail', key: 'price_retail', width: '130px' },
        { title: 'ราคาขาย (ส่ง)', dataIndex: 'price_wholesale', key: 'price_wholesale', width: '130px' },
        // { title: 'ผู้ขายส่งสินค้า', dataIndex: 'vendor_name', key: 'vendor_name' },
        { title: 'การดำเนินการ', dataIndex: 'operation', width: '150px', fixed: 'right', render: (_, record) =>
            data.length >= 1 ? ( 
                <div className='iconOperation'>
                    <FontAwesomeIcon icon="eye" title="ดูรายละเอียด" onClick={() => { onShowDetail('VIEW', record) }} />
                    <FontAwesomeIcon icon="pen-to-square" title="แก้ไข" onClick={() => { onShowDetail('EDIT', record); }} />
                    <FontAwesomeIcon icon="trash-can" title="ลบ" onClick={() => { onDelete(record.id) }} />
                </div>
            ) : null
        }
    ]

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [productTypeFilter, setProductTypeFilter] = useState(0);

    const debounceTimerRef = React.useRef<any>(null);

    const loadProducts = async (
        pageNo = 1,
        search = searchKeyword,
        productType = productTypeFilter,
        size = pageSize,
        filter: 'ALL' | 'OUT' | 'LOW' | 'NORMAL' | 'NEVER_STOCKED' = stockFilter,
    ) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api_product('LIST', {
                page: pageNo,
                page_size: size,
                search: search.trim() || undefined,
                product_type: productType > 0 ? productType : undefined,
                stock_filter: filter === 'ALL' ? undefined : filter
            });
            if (res && res.items) {
                setData(res.items);
                setPage(res.pagination?.page || pageNo);
                setPageSize(res.pagination?.page_size || size);
                setTotalCount(res.pagination?.total || 0);
                setTotalPages(res.pagination?.total_pages || 1);
                // The counts describe the whole catalogue and deliberately
                // ignore the active stock filter, so the cards keep working
                // as the way to switch between the four views.
                if (res.counts) setCounts(res.counts);
            } else if (Array.isArray(res)) {
                setData(res);
                setTotalCount(res.length);
                setTotalPages(1);
            }
        } catch (err: any) {
            console.error('Failed to load products:', err);
            setError('โหลดข้อมูลสินค้าไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อ');
            setData([]);
        } finally {
            setLoading(false);
        }
    }

    const init = async () => {
        loadProducts(1, '', 0);
    }

    const onSearchChange = (val: string) => {
        setSearchKeyword(val);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            loadProducts(1, val, productTypeFilter);
        }, 300);
    }

    const onProductTypeChange = (typeId: number) => {
        setProductTypeFilter(typeId);
        loadProducts(1, searchKeyword, typeId);
    }

    const onPageChange = (newPage: number) => {
        loadProducts(newPage, searchKeyword, productTypeFilter);
    }

    const onStockFilterChange = (filter: 'ALL' | 'OUT' | 'LOW' | 'NORMAL' | 'NEVER_STOCKED') => {
        setStockFilter(filter);
        setPage(1);
        loadProducts(1, searchKeyword, productTypeFilter, pageSize, filter);
    }

    // Server-side paging, so the size changer has to re-query rather than
    // slice a list the client already holds. It was `showSizeChanger: false`
    // with a hardcoded 200 rows per request, which is why this screen was
    // the one page with no rows-per-page control at all.
    const paginationConfig = {
        current: page,
        pageSize: pageSize,
        total: totalCount,
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        selectComponentClass: PageSizeSelect,
        locale: { items_per_page: '/ หน้า' },
        showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ (หน้า ${page}/${totalPages})`,
        onChange: (newPage: number, newPageSize?: number) => {
            if (newPageSize && newPageSize !== pageSize) {
                // A different page size makes the current page number
                // meaningless, so go back to the first one and re-query.
                setPageSize(newPageSize);
                setPage(1);
                loadProducts(1, searchKeyword, productTypeFilter, newPageSize);
                return;
            }
            onPageChange(newPage);
        },
        responsive: true,
        size: 'small' as const
    };

    const getConfigModal = (titile: string, content: string) => {
        return {
            title: titile,
            icon: <ExclamationCircleFilled rev={undefined} />,
            content: content,
            okText: 'ตกลง',
            cancelText: 'ยกเลิก',
            centered: true
        }
    }

    const onShowDetail = async (mode: string, productData?: product) => {
        if (mode === 'ADD' || mode === 'COPY') {
            setTitle('เพิ่มสินค้า')
        } else if (mode === 'EDIT') {
            setTitle('แก้ไขสินค้า')    
        } else if (mode === 'VIEW') {
            setTitle('รายละเอียดสินค้า')
        }

        setMode(mode);
        let tempData = productData !== undefined && productData !== null ? productData : initProduct;
        if (tempData.id > 0) {
            let getProduct = await api_product('GET', {}, tempData.id);
            setProduct(getProduct);
        } else {
            setProduct(tempData);
        }
        setOpen(true);
    };

    const onCloseDetail = () => {
        setOpen(false);
    };

    const onSave = async () => {
        let operation = 'INSERT';
        let key_id = undefined;
        let body = JSON.parse(JSON.stringify(product));
        if (product.id > 0) {
            operation = 'UPDATE';
            key_id = product.id;
        }

        let isAddMode = !product.id || product.id <= 0;
        let missingFields: string[] = [];

        if (isAddMode) {
            if (body.has_barcode !== false && isEmpty(body['product_code'])) {
                missingFields.push('รหัสสินค้า/บาร์โค้ด (หรือเลือก "ไม่มีบาร์โค้ด" เพื่อสร้างรหัสอัตโนมัติ)');
            }
        } else {
            if (isEmpty(body['product_code'])) {
                missingFields.push('รหัสสินค้า');
            }
        }

        if (isEmpty(body['product_name'])) {
            missingFields.push('ชื่อสินค้า');
        }

        if (!body['product_type'] || Number(body['product_type']) <= 0) {
            missingFields.push('ประเภทสินค้า');
        }

        if (!body['product_unit'] || Number(body['product_unit']) <= 0) {
            missingFields.push('หน่วยสินค้า');
        }

        if (isEmpty(body['product_cost'])) {
            missingFields.push('ราคาต้นทุน');
        }

        if (isEmpty(body['price_retail'])) {
            missingFields.push('ราคาขายปลีก');
        }

        if (missingFields.length > 0) {
            Modal.warning({
                ...getConfigModal('ข้อมูลไม่ครบถ้วน', `กรุณากรอกข้อมูลที่จำเป็น (*) ให้ครบถ้วน:\n• ${missingFields.join('\n• ')}`)
            });
            return;
        }

        body['product_cost'] = isEmpty(body['product_cost']) ? null : body['product_cost']
        body['price_retail'] = isEmpty(body['price_retail']) ? null : body['price_retail']
        body['price_wholesale'] = isEmpty(body['price_wholesale']) ? null : body['price_wholesale']
        body['price_retail_member'] = isEmpty(body['price_retail_member']) ? null : body['price_retail_member']
        body['price_wholesale_member'] = isEmpty(body['price_wholesale_member']) ? null : body['price_wholesale_member']
        body['init_order_amount'] = isEmpty(body['init_order_amount']) ? null : body['init_order_amount']
        body['point_purchase'] = isEmpty(body['point_purchase']) ? null : body['point_purchase']
        body['limit_quantity'] = isEmpty(body['limit_quantity']) ? null : body['limit_quantity']
        body['point'] = isEmpty(body['point']) ? null : body['point']
        body['vendor'] = body['vendor'] && body['vendor'].length > 0 ? JSON.stringify(body['vendor']).replace('[', '').replace(']', '') + "," : ""
        body['remark'] = isEmpty(body['remark']) ? null : body['remark']
        body['vat'] = body['vat'] < 0 ? null : body['vat']
        body['tier_price'] = isEmpty(body['tier_price']) ? "[]" : body['tier_price']

        let res = await api_product(operation, body, key_id);
        if (res === 1) {
            setOpen(false);
            Modal.success({
                ...getConfigModal('สำเร็จ', operation === 'UPDATE' ? 'แก้ไขข้อมูลสำเร็จแล้ว' : 'เพิ่มข้อมูลสำเร็จแล้ว'),
                ...{ onOk() { init(); }}
            });
        } else {
            Modal.error({
                ...getConfigModal('ผิดพลาด', operation === 'UPDATE' ? 'แก้ไขข้อมูลไม่สำเร็จ กรุณาตรวจสอบการเชื่อมต่อหรือลองใหม่อีกครั้ง' : 'เพิ่มข้อมูลไม่สำเร็จ (อาจเกิดจากรหัสสินค้าซ้ำหรือระบบขัดข้อง กรุณาลองใหม่อีกครั้ง)'),
            });
        }

    }

    const onDelete = async (key_id: number) => {
        Modal.confirm({
            ...getConfigModal('แจ้งเตือน', 'คุณต้องการลบข้อมูลนี้หรือไม่ ?'),
            ...{
                async onOk() { 
                    try {
                        let res = await api_product('DELETE', {}, key_id);
                        if (res === 1) {
                            Modal.success({
                                ...getConfigModal('สำเร็จ', 'ลบข้อมูลสำเร็จแล้ว'),
                                ...{ onOk() { init(); }}
                            });
                        } else {
                            Modal.error({
                                ...getConfigModal('ผิดพลาด', 'ลบข้อมูลไม่สำเร็จ'),
                            });
                        }
                    } catch (err: any) {
                        // Backend answers a record that is already gone with a
                        // specific 404 and a sentence written for the user. Showing
                        // only "ลบข้อมูลไม่สำเร็จ" threw that away and left them
                        // guessing whether it was the network, their permissions, or
                        // a colleague who deleted it first.
                        Modal.error({
                            ...getConfigModal('ผิดพลาด', deleteFailureMessage(err, 'ลบข้อมูลไม่สำเร็จ')),
                        });
                    }
                }
            }
        });
    }

    const onDataDetailChange = (payload: any) => {
        setProduct(payload)
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <div className="pm-page-container">
            <div className="pm-header-row">
                <div className="pm-header-title-box">
                    <h2>
                        <FontAwesomeIcon icon="box-archive" style={{ color: 'var(--pm-primary, #FF7A59)' }} />
                        จัดการสินค้า (Product Catalog & Inventory)
                    </h2>
                    <p>รายการสินค้าทั้งหมดในระบบ ข้อมูลสต๊อกคงเหลือ และเกณฑ์แจ้งเตือนสินค้าใกล้หมด</p>
                </div>
                <div className="pm-header-actions">
                    <Button
                        type="primary"
                        className="pm-btn-primary"
                        icon={<FontAwesomeIcon icon="bolt" />}
                        onClick={() => navigate('/purchase/order')}
                        data-testid="btn-quick-create-po"
                    >
                        ⚡ สั่งซื้อสินค้า (PO)
                    </Button>
                </div>
            </div>

            <div className="pm-stats-grid">
                <div
                    className={`pm-stat-card clickable ${stockFilter === 'ALL' ? 'active' : ''}`}
                    onClick={() => onStockFilterChange('ALL')}
                    data-testid="tab-stock-all"
                >
                    <span className="pm-stat-label">📦 สินค้าทั้งหมด</span>
                    <span className="pm-stat-value" data-testid="count-all">{counts.total || totalCount} <span className="pm-stat-unit">รายการ</span></span>
                    <span className="pm-stat-sub">แสดงสินค้าทุกประเภทในระบบ</span>
                </div>
                {/* Out of stock is a fact -- it warns whether or not anyone
                    set a warning level, which is why it is its own card and
                    is decided before "ใกล้หมด". */}
                <div
                    className={`pm-stat-card clickable ${stockFilter === 'OUT' ? 'active' : ''}`}
                    onClick={() => onStockFilterChange('OUT')}
                    data-testid="tab-stock-out"
                >
                    <span className="pm-stat-label">🔴 สินค้าหมด</span>
                    <span className="pm-stat-value danger" data-testid="count-out">{counts.out} <span className="pm-stat-unit">รายการ</span></span>
                    <span className="pm-stat-sub">เคยลงสต๊อกแล้ว และเหลือ 0</span>
                </div>
                <div
                    className={`pm-stat-card clickable ${stockFilter === 'LOW' ? 'active' : ''}`}
                    onClick={() => onStockFilterChange('LOW')}
                    data-testid="tab-stock-low"
                >
                    <span className="pm-stat-label">⚠️ สินค้าใกล้หมด</span>
                    <span className="pm-stat-value danger" data-testid="count-low">{counts.low} <span className="pm-stat-unit">รายการ</span></span>
                    <span className="pm-stat-sub">ยังมีของ แต่ถึงจุดเตือนที่ตั้งไว้</span>
                </div>
                <div
                    className={`pm-stat-card clickable ${stockFilter === 'NORMAL' ? 'active' : ''}`}
                    onClick={() => onStockFilterChange('NORMAL')}
                    data-testid="tab-stock-normal"
                >
                    <span className="pm-stat-label">🟢 สต๊อกปกติ</span>
                    <span className="pm-stat-value success" data-testid="count-normal">{counts.normal} <span className="pm-stat-unit">รายการ</span></span>
                    <span className="pm-stat-sub">มีของ และยังไม่ถึงจุดเตือน (หรือไม่ได้ตั้งไว้)</span>
                </div>
                {/* The state that was hiding inside "ใกล้หมด": a product with
                    no stock row has never been received. It is not something
                    to reorder -- it is catalogue import that is not finished,
                    and showing it separately turns 2,584 false alarms into a
                    progress figure. */}
                <div
                    className={`pm-stat-card clickable ${stockFilter === 'NEVER_STOCKED' ? 'active' : ''}`}
                    onClick={() => onStockFilterChange('NEVER_STOCKED')}
                    data-testid="tab-stock-never"
                >
                    <span className="pm-stat-label">📥 ยังไม่ได้ลงสต๊อก</span>
                    <span className="pm-stat-value" data-testid="count-never">{counts.never_stocked} <span className="pm-stat-unit">รายการ</span></span>
                    <span className="pm-stat-sub">นำเข้ารายการแล้ว แต่ยังไม่เคยรับเข้า</span>
                </div>
            </div>

            {error ? (
                <div style={{ margin: '10px 0 15px', padding: '12px 18px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} data-testid="product-load-error">
                    <span style={{ color: '#DC2626', fontWeight: 600 }}>⚠️ {error}</span>
                    <Button type="primary" danger size="small" onClick={init} style={{ borderRadius: '6px' }}>
                        ลองใหม่อีกครั้ง (Retry)
                    </Button>
                </div>
            ) : null}

            <CommonTableList
                page='product'
                placeholder={placeholder_txt}
                columns={columns}
                data={[...filteredData]}
                filterField={filterField}
                onAdd={() => { onShowDetail('ADD') }}
                serverPagination={true}
                pagination={paginationConfig}
                loading={loading}
                onSearchChange={onSearchChange}
                onProductTypeChange={onProductTypeChange}
                hidePrint={true}
            />
            <CommonDetail width='40%' mode={mode} title={title} open={open} onClosed={onCloseDetail} onSaved={onSave} element={ <ProductDetail product={{...product}} onChange={onDataDetailChange} /> } />
        </div>
    );
}

export default Product;